import { AssetConstants, MediaConstants } from 'src/constants';
import { VALIDATION_CODES } from 'src/constants/validationCodes';
import { ValidationError } from 'src/errors/CustomErrors';
import { formatDuration } from 'src/utils/assetInput/assetInputUtils';
import { timeToSeconds } from 'src/utils/DateTimeUtils';
import { withTimeout } from 'src/utils/MediaUtils';

import { loadVideoDimensions } from '../MediaUtils';
import { DimensionSpecs, VideoLengthSpecs } from '../Types';
import { validateAspectRatio, validateDimensions } from '../ValidationUtils';

/**
 * Validates time format (MM:SS)
 * @param timeStr - Time string to validate
 * @returns boolean indicating if format is valid
 */
export const validateTimeFormat = (timeStr: string): boolean => {
  if (!timeStr) return true; // Empty is valid
  const timeRegex = /^([0-5][0-9]):([0-5][0-9])$/; // Require zero-padded format
  return timeRegex.test(timeStr);
};

/**
 * Video duration validation result
 */
export interface VideoDurationValidationResult {
  isValid: boolean;
  endError: string;
  durationError: string;
}

/**
 * Validates video duration input times
 * @param startTime - Start time in MM:SS format
 * @param endTime - End time in MM:SS format
 * @param videoDuration - Total video duration in seconds (optional)
 * @param minDuration - Minimum required duration in seconds
 * @param maxDuration - Maximum allowed duration in seconds
 * @returns Validation result with errors and validity status
 */
export const validateVideoDurationInput = (
  startTime: string,
  endTime: string,
  videoDuration?: number,
  minDuration: number = AssetConstants.VIDEO_DURATION.MIN_DURATION,
  maxDuration: number = AssetConstants.VIDEO_DURATION.MAX_DURATION,
): VideoDurationValidationResult => {
  let endError = '';
  let durationError = '';
  let isValid = false;

  if (startTime && endTime) {
    const startSeconds = timeToSeconds(startTime);
    const endSeconds = timeToSeconds(endTime);
    const duration = endSeconds - startSeconds;

    if (startSeconds >= endSeconds) {
      endError = 'End time must be after start time';
    } else if (videoDuration && endSeconds > Math.round(videoDuration)) {
      endError = `End time cannot exceed video duration (${formatDuration(videoDuration)})`;
    } else if (videoDuration && videoDuration < minDuration) {
      durationError = `Video is too short (${formatDuration(videoDuration)}). Minimum required: ${formatDuration(minDuration)}`;
    } else if (duration < minDuration) {
      durationError = `Duration must be at least ${formatDuration(minDuration)}`;
    } else if (duration > maxDuration) {
      durationError = `Duration must not exceed ${formatDuration(maxDuration)} (current: ${formatDuration(duration)})`;
    } else {
      isValid = true;
    }
  }

  return { isValid, endError, durationError };
};

/**
 * Validates the length of a video file against specifications
 *
 * @param fileObject - The video file to validate
 * @param specs - Specifications for validation
 * @returns Promise that resolves with video duration if validation passes, throws ValidationError otherwise
 */
export const validateVideoLength = (
  fileObject: File,
  specs: VideoLengthSpecs,
  timeoutMs = MediaConstants.TIMEOUTS.MEDIA_VIDEO_LOAD,
): Promise<number> => {
  // Return a rejected promise for file type errors instead of throwing synchronously
  if (!fileObject.type.startsWith(MediaConstants.MIME.PREFIXES.VIDEO)) {
    return Promise.reject(
      new ValidationError(
        `File type ${fileObject.type} is not a video format`,
        VALIDATION_CODES.INVALID_FILE_TYPE,
      ),
    );
  }

  const video = document.createElement(MediaConstants.ELEMENTS.VIDEO);
  const objectUrl = URL.createObjectURL(fileObject);

  const loadPromise = new Promise<number>((resolve, reject) => {
    video.onloadedmetadata = () => {
      try {
        const duration = video.duration;
        if (isNaN(duration) || duration <= 0) {
          reject(new ValidationError('Invalid video duration'));
          return;
        }
        if (
          specs.maxDurationInSeconds &&
          duration >
            specs.maxDurationInSeconds +
              MediaConstants.VALIDATION.VIDEO_DURATION_PRECISION_TOLERANCE
        ) {
          reject(
            new ValidationError(
              `Video duration (${Math.round(duration)}s) exceeds maximum allowed duration of ${specs.maxDurationInSeconds}s`,
              VALIDATION_CODES.VIDEO_TOO_LONG,
            ),
          );
          return;
        }
        if (
          duration <
          specs.minDurationInSeconds - MediaConstants.VALIDATION.VIDEO_DURATION_PRECISION_TOLERANCE
        ) {
          reject(
            new ValidationError(
              `Video duration (${Math.round(duration)}s) is less than minimum required duration of ${specs.minDurationInSeconds}s`,
              VALIDATION_CODES.VIDEO_TOO_SHORT,
            ),
          );
          return;
        }
        resolve(duration);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new ValidationError('Failed to load video'));
    };

    video.preload = MediaConstants.VIDEO.DEFAULT_PRELOAD;
    video.src = objectUrl;
  });

  return withTimeout(loadPromise, timeoutMs);
};

/**
 * Validates video dimensions against specifications
 * @throws {ValidationError} When dimensions don't meet requirements
 */
export const validateVideoDimensions = async (
  videoFile: File,
  specs: DimensionSpecs,
): Promise<void> => {
  if (!videoFile.type.startsWith(MediaConstants.MIME.PREFIXES.VIDEO)) {
    throw new ValidationError('File is not a video', VALIDATION_CODES.INVALID_FILE_TYPE);
  }

  const objectUrl = URL.createObjectURL(videoFile);
  try {
    const dimensions = await loadVideoDimensions(objectUrl);
    if (dimensions.width === 0 || dimensions.height === 0) {
      throw new ValidationError('Could not determine file dimensions');
    }
    validateDimensions(dimensions.width, dimensions.height, specs);
    if (specs?.aspectRatio != null && specs?.aspectRatioTolerance != null) {
      validateAspectRatio(
        dimensions.width,
        dimensions.height,
        specs.aspectRatio,
        specs.aspectRatioTolerance,
      );
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Validates if video duration between start and end time meets minimum requirements
 * @param startTime - Start time in MM:SS format
 * @param endTime - End time in MM:SS format
 * @returns boolean indicating if duration is valid
 */
export const isValidVideoDuration = (startTime: string, endTime: string): boolean => {
  if (!startTime || !endTime) return false;

  const duration = timeToSeconds(endTime) - timeToSeconds(startTime);
  return duration >= AssetConstants.VIDEO_DURATION.MIN_DURATION;
};
